import * as Blockly from 'blockly';
import { dictRegistry } from './openfoam.blocks.js';

/**
 * Utility to create and initialize a Blockly block with path tracking
 */
export function createBlock(workspace, blockType, path = '') {
  try {
    if (!Blockly.Blocks[blockType]) {
      throw new Error(`Block type ${blockType} not defined`);
    }
    const block = workspace.newBlock(blockType);
    block.initSvg();
    block.render();
    
    // Store path information on block for debugging/tracking
    if (path) {
      block.pathInfo = path;
      console.log(`Created block ${blockType} at path: ${path}`);
    }
    
    return block;
  } catch (error) {
    console.error(`Error creating block ${blockType} at path ${path}:`, error);
    return null;
  }
}

/**
 * Utility to select the first valid block type from a prioritized list
 */
export function selectBlockType(blockTypes) {
  return blockTypes.find(type => Blockly.Blocks[type]) || null;
}

/**
 * Utility to connect a value block to a block's input
 */
export function connectValueBlock(block, valueBlock, entryKey, path = '') {
  try {
    const valueInput = block.getInput('VALUE') || block.getInput('VAL');
    if (!valueInput) {
      console.warn(`No VALUE or VAL input found for block with key ${entryKey} at path ${path}`);
      return false;
    }

    if (!valueBlock || !valueBlock.outputConnection) {
      console.warn(`No valid output connection for value block of ${entryKey} at path ${path}`);
      return false;
    }

    const originalCheck = valueInput.connection.getCheck();
    valueInput.connection.setCheck(null);
    valueInput.connection.connect(valueBlock.outputConnection);
    valueInput.connection.setCheck(originalCheck);
    console.log(`Connected value block for ${entryKey} at path ${path}`);
    return true;
  } catch (error) {
    console.error(`Failed to connect value block for ${entryKey} at path ${path}:`, error);
    return false;
  }
}

/**
 * Create tuple block
 */
export function createTupleBlock(tuple, workspace, path = '') {
  try {
    const block = createBlock(workspace, 'common_tuple', path);
    if (!block) return null;
    
    block.setFieldValue(tuple.name, 'TUPLE_NAME');

    const stmtInput = block.getInput('ITEMS');
    if (stmtInput && tuple.items && tuple.items.length > 0) {
      let prevBlock = null;
      for (const [index, item] of tuple.items.entries()) {
        const itemPath = `${path}_item_${index}`;
        const itemBlock = createBlock(workspace, 'common_tuple_item', itemPath);
        if (!itemBlock) continue;
        
        const valueBlock = typeof item === 'number' 
          ? createBlock(workspace, 'common_value_number', `${itemPath}_value`) 
          : createBlock(workspace, 'common_value_string', `${itemPath}_value`);
          
        if (valueBlock) {
          valueBlock.setFieldValue(String(item), 'VALUE');
          const valueInput = itemBlock.getInput('VALUE');
          if (valueInput) {
            valueInput.connection.connect(valueBlock.outputConnection);
          }
        }

        if (!prevBlock) {
          itemBlock.previousConnection.connect(stmtInput.connection);
        } else {
          itemBlock.previousConnection.connect(prevBlock.nextConnection);
        }
        prevBlock = itemBlock;
      }
    }

    return block;
  } catch (error) {
    console.error(`Error in createTupleBlock at path ${path}:`, error);
    return null;
  }
}

/**
 * Create block section block
 */
export function createBlockSectionBlock(section, workspace, path = '') {
  try {
    console.log(`Creating block section for: ${section.name} at path: ${path}`);

    if (!Blockly.Blocks['common_block_section']) {
      console.error('common_block_section block type not defined');
      const block = createBlock(workspace, 'common_entry', path);
      if (!block) return null;
      
      block.setFieldValue(section.name, 'KEY');
      
      const valueBlock = createBlock(workspace, 'common_value_string', `${path}_value`);
      if (valueBlock) {
        const entriesStr = Array.isArray(section.entries) 
          ? `[${section.entries.join(', ')}]` 
          : '[]';
        valueBlock.setFieldValue(entriesStr, 'VALUE');
        const valueInput = block.getInput('VALUE');
        if (valueInput) {
          valueInput.connection.connect(valueBlock.outputConnection);
        }
      }
      return block;
    }

    const block = createBlock(workspace, 'common_block_section', path);
    if (!block) return null;
    
    block.setFieldValue(section.name, 'SECTION_NAME');

    const entriesInput = block.getInput('ENTRIES');
    if (entriesInput && section.entries && section.entries.length > 0) {
      let prevBlock = null;

      for (const [index, entry] of section.entries.entries()) {
        const entryPath = `${path}_entry_${index}`;
        const entryBlock = createBlock(workspace, 'common_block_entry', entryPath);
        if (!entryBlock) continue;

        const valueBlock = typeof entry === 'number'
          ? createBlock(workspace, 'common_value_number', `${entryPath}_value`)
          : createBlock(workspace, 'common_value_string', `${entryPath}_value`);

        if (valueBlock) {
          valueBlock.setFieldValue(String(entry), 'VALUE');
          const valueInput = entryBlock.getInput('VALUE');
          if (valueInput) {
            valueInput.connection.connect(valueBlock.outputConnection);
          }
        }

        if (!prevBlock) {
          entryBlock.previousConnection.connect(entriesInput.connection);
        } else {
          entryBlock.previousConnection.connect(prevBlock.nextConnection);
        }
        prevBlock = entryBlock;
      }
    }

    return block;
  } catch (error) {
    console.error(`Error in createBlockSectionBlock at path ${path}:`, error);
    return null;
  }
}

/**
 * Create macro block
 */
export function createMacroBlock(macro, workspace, path = '') {
  try {
    const block = createBlock(workspace, 'common_macro', path);
    if (!block) return null;
    
    block.setFieldValue(macro.directive, 'DIRECTIVE');

    const valueBlock = createBlock(workspace, 'common_value_string', `${path}_value`);
    if (valueBlock) {
      valueBlock.setFieldValue(macro.argument, 'VALUE');
      const argumentInput = block.getInput('ARGUMENT');
      if (argumentInput) {
        argumentInput.connection.connect(valueBlock.outputConnection);
      }
    }

    return block;
  } catch (error) {
    console.error(`Error in createMacroBlock at path ${path}:`, error);
    return null;
  }
}

/**
 * Create codestream block
 */
export function createCodestreamBlock(codestream, workspace, path = '') {
  try {
    const block = createBlock(workspace, 'common_codestream', path);
    if (!block) return null;
    
    block.setFieldValue(codestream.content, 'CONTENT');
    return block;
  } catch (error) {
    console.error(`Error in createCodestreamBlock at path ${path}:`, error);
    return null;
  }
}

/**
 * Create vector3 block
 */
export function createVector3Block(vector3, workspace, path = '', dictName = 'common') {
  try {
    console.log(`Creating vector3 block with components: ${vector3.components} at path: ${path} for dict: ${dictName}`);

    // Use specialized decomposition vector for decomposeParDict
    let blockType = 'common_vector3';
    let numberBlockType = 'common_value_number';
    
    if (dictName === 'decomposeParDict') {
      blockType = 'decomposeParDict_decomp_vector';
      numberBlockType = 'decomposeParDict_value_number';
      console.log(`Using specialized decomposition vector block for decomposeParDict: ${blockType}`);
    }

    const block = createBlock(workspace, blockType, path);
    if (!block) return null;

    const inputs = ['X', 'Y', 'Z'];
    vector3.components.forEach((value, index) => {
      const input = block.getInput(inputs[index]);
      if (input) {
        const valueBlock = createBlock(workspace, numberBlockType, `${path}_${inputs[index].toLowerCase()}`);
        if (valueBlock) {
          valueBlock.setFieldValue(String(value), 'VALUE');
          input.connection.connect(valueBlock.outputConnection);
        }
      } else {
        console.warn(`Input ${inputs[index]} not found on ${blockType} block at path: ${path}`);
      }
    });

    return block;
  } catch (error) {
    console.error(`Error creating vector3 block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create vector3list block
 */
export function createVector3ListBlock(vector3list, workspace, path = '', dictName = 'common') {
  try {
    console.log(`Creating vector3list block for: ${vector3list.name} at path: ${path} for dict: ${dictName}`);

    const block = createBlock(workspace, 'common_vector3list', path);
    if (!block) return null;
    
    block.setFieldValue(vector3list.name || 'unnamed', 'LIST_NAME');

    const vectorsInput = block.getInput('VECTORS');
    if (vectorsInput && vector3list.vectors && vector3list.vectors.length > 0) {
      let prevBlock = null;

      for (const [index, vector] of vector3list.vectors.entries()) {
        const vectorPath = `${path}_vector_${index}`;
        const vectorBlock = createVector3Block(vector, workspace, vectorPath, dictName);
        if (vectorBlock) {
          if (!prevBlock) {
            vectorBlock.previousConnection.connect(vectorsInput.connection);
          } else if (prevBlock.nextConnection && vectorBlock.previousConnection) {
            vectorBlock.previousConnection.connect(prevBlock.nextConnection);
          }
          prevBlock = vectorBlock;
        }
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating vector3list block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create connectivity4 block
 */
export function createConnectivity4Block(connectivity, workspace, path = '') {
  try {
    console.log(`Creating connectivity4 block with indices: ${connectivity.indices} at path: ${path}`);

    const block = createBlock(workspace, 'common_connectivity4', path);
    if (!block) return null;

    const inputs = ['V0', 'V1', 'V2', 'V3'];
    connectivity.indices.forEach((value, index) => {
      const input = block.getInput(inputs[index]);
      if (input) {
        const valueBlock = createBlock(workspace, 'common_value_number', `${path}_${inputs[index].toLowerCase()}`);
        if (valueBlock) {
          valueBlock.setFieldValue(String(value), 'VALUE');
          input.connection.connect(valueBlock.outputConnection);
        }
      } else {
        console.warn(`Input ${inputs[index]} not found on common_connectivity4 block at path: ${path}`);
      }
    });

    return block;
  } catch (error) {
    console.error(`Error creating connectivity4 block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create connectivity4list block
 */
export function createConnectivity4ListBlock(connectivityList, workspace, path = '') {
  try {
    console.log(`Creating connectivity4list block for: ${connectivityList.name} at path: ${path}`);

    const block = createBlock(workspace, 'common_connectivity4list', path);
    if (!block) return null;
    
    block.setFieldValue(connectivityList.name || 'unnamed', 'LIST_NAME');

    const connectionsInput = block.getInput('CONNECTIONS');
    if (connectionsInput && connectivityList.connections && connectivityList.connections.length > 0) {
      let prevBlock = null;

      for (const [index, conn] of connectivityList.connections.entries()) {
        const connPath = `${path}_connection_${index}`;
        const connBlock = createConnectivity4Block(conn, workspace, connPath);
        if (connBlock) {
          if (!prevBlock) {
            connBlock.previousConnection.connect(connectionsInput.connection);
          } else if (prevBlock.nextConnection && connBlock.previousConnection) {
            connBlock.previousConnection.connect(prevBlock.nextConnection);
          }
          prevBlock = connBlock;
        }
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating connectivity4list block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create list block
 */
export function createListBlock(list, workspace, path = '') {
  try {
    console.log(`Creating list block at path: ${path}:`, list);

    const block = createBlock(workspace, 'common_list', path);
    if (!block) return null;

    const itemsInput = block.getInput('ITEMS');
    if (itemsInput && list.items && list.items.length > 0) {
      let prevBlock = null;

      for (const [index, item] of list.items.entries()) {
        const itemPath = `${path}_item_${index}`;
        const itemBlock = createBlock(workspace, 'common_list_item', itemPath);
        if (!itemBlock) continue;

        let valueBlock;
        if (typeof item === 'object' && item !== null && item.type) {
          // Complex item - will be handled by BFS traversal
          console.log(`Complex list item of type: ${item.type} will be processed separately`);
          valueBlock = createBlock(workspace, 'common_value_string', `${itemPath}_placeholder`);
          if (valueBlock) {
            valueBlock.setFieldValue(`{${item.type}}`, 'VALUE');
          }
        } else {
          // Simple item
          const valueType = typeof item === 'number' ? 'common_value_number' : 'common_value_string';
          valueBlock = createBlock(workspace, valueType, `${itemPath}_value`);
          if (valueBlock) {
            valueBlock.setFieldValue(String(item), 'VALUE');
          }
        }

        if (valueBlock && valueBlock.outputConnection) {
          const valueInput = itemBlock.getInput('VALUE');
          if (valueInput) {
            valueInput.connection.connect(valueBlock.outputConnection);
          }
        }

        if (!prevBlock) {
          itemBlock.previousConnection.connect(itemsInput.connection);
        } else {
          itemBlock.previousConnection.connect(prevBlock.nextConnection);
        }
        prevBlock = itemBlock;
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating list block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create named list block
 */
export function createNamedListBlock(namedList, workspace, path = '') {
  try {
    console.log(`Creating named list block at path: ${path}:`, namedList);

    const block = createBlock(workspace, 'common_list', path);
    if (!block) return null;
    
    block.setFieldValue(namedList.name || 'unnamed', 'LIST_NAME');

    const itemsInput = block.getInput('ITEMS');
    if (itemsInput && namedList.strings && namedList.strings.length > 0) {
      let prevBlock = null;

      for (const [index, item] of namedList.strings.entries()) {
        const itemPath = `${path}_string_${index}`;
        const itemBlock = createBlock(workspace, 'common_list_item', itemPath);
        if (!itemBlock) continue;

        let valueBlock;
        if (typeof item === 'object' && item !== null && item.type) {
          // Complex item - will be handled by BFS traversal
          console.log(`Complex named list item of type: ${item.type} will be processed separately`);
          valueBlock = createBlock(workspace, 'common_value_string', `${itemPath}_placeholder`);
          if (valueBlock) {
            valueBlock.setFieldValue(`{${item.type}}`, 'VALUE');
          }
        } else {
          // Simple item
          const valueType = typeof item === 'number' ? 'common_value_number' : 'common_value_string';
          valueBlock = createBlock(workspace, valueType, `${itemPath}_value`);
          if (valueBlock) {
            valueBlock.setFieldValue(String(item), 'VALUE');
          }
        }

        if (valueBlock && valueBlock.outputConnection) {
          const valueInput = itemBlock.getInput('VALUE');
          if (valueInput) {
            valueInput.connection.connect(valueBlock.outputConnection);
          }
        }

        if (!prevBlock) {
          itemBlock.previousConnection.connect(itemsInput.connection);
        } else {
          itemBlock.previousConnection.connect(prevBlock.nextConnection);
        }
        prevBlock = itemBlock;
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating named list block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create namedDictList block
 */
export function createNamedDictListBlock(namedDictList, dictName, workspace, path = '') {
  try {
    console.log(`Creating namedDictList block at path: ${path}:`, namedDictList);

    const block = createBlock(workspace, 'common_named_dict_list', path);
    if (!block) return null;
    
    block.setFieldValue(namedDictList.name || 'unnamed', 'LIST_NAME');

    // Note: Dictionary entries will be handled by BFS traversal
    console.log(`Named dict list contains ${namedDictList.dictionaries?.length || 0} dictionaries - will be processed by BFS`);

    return block;
  } catch (error) {
    console.error(`Error creating namedDictList block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create FoamFile dictionary block
 */
export function createFoamFileDictionaryBlock(foamFileDict, dictName, workspace, path = '') {
  try {
    console.log(`Creating FoamFile dictionary block at path: ${path}`);

    if (!Blockly.Blocks['common_foamfile_dict']) {
      console.error('common_foamfile_dict block type not defined');
      const block = createBlock(workspace, 'common_dict', path);
      if (!block) return null;
      
      block.setFieldValue('FoamFile', 'DICT_NAME');

      // Note: Entries will be handled by BFS traversal
      return block;
    }

    const block = createBlock(workspace, 'common_foamfile_dict', path);
    if (!block) return null;

    const foamFileEntries = {
      version: '2.0',
      format: 'ascii',
      class: 'dictionary',
      location: 'system',
      object: dictName || 'controlDict'
    };

    if (foamFileDict.entries) {
      foamFileDict.entries.forEach(entry => {
        if (entry.key in foamFileEntries) {
          foamFileEntries[entry.key] = entry.value;
        }
      });
    }

    for (const key of ['version', 'format', 'class', 'location', 'object']) {
      const input = block.getInput(key.toUpperCase());
      if (input) {
        const valueBlock = createBlock(workspace, 'common_value_string', `${path}_${key}`);
        if (valueBlock) {
          valueBlock.setFieldValue(String(foamFileEntries[key]), 'VALUE');
          input.connection.connect(valueBlock.outputConnection);
        }
      }
    }

    return block;
  } catch (error) {
    console.error(`Error in createFoamFileDictionaryBlock at path ${path}:`, error);
    return null;
  }
}

/**
 * Create a value block for a vector3
 */
export function createVector3ValueBlock(vector3, workspace, path = '', dictName = 'common') {
  try {
    console.log(`Creating vector3 value block with components: ${vector3.components} at path: ${path} for dict: ${dictName}`);
    
    // Use specialized decomposition vector for decomposeParDict
    let blockType = 'common_value_vector3';
    let numberBlockType = 'common_value_number';
    
    if (dictName === 'decomposeParDict') {
      blockType = 'decomposeParDict_decomp_vector';
      numberBlockType = 'decomposeParDict_value_number';
      console.log(`Using specialized decomposition vector value block for decomposeParDict: ${blockType}`);
    }
    
    const block = createBlock(workspace, blockType, path);
    if (!block) return null;
    
    if (dictName === 'decomposeParDict') {
      // For decomp_vector, we need to connect number blocks to inputs
      const inputs = ['X', 'Y', 'Z'];
      vector3.components.forEach((value, index) => {
        if (index < inputs.length) {
          const input = block.getInput(inputs[index]);
          if (input) {
            const valueBlock = createBlock(workspace, numberBlockType, `${path}_${inputs[index].toLowerCase()}`);
            if (valueBlock) {
              valueBlock.setFieldValue(String(value), 'VALUE');
              input.connection.connect(valueBlock.outputConnection);
            }
          }
        }
      });
    } else {
      // For common_value_vector3, set field values directly
      block.setFieldValue(String(vector3.components[0]), 'X');
      block.setFieldValue(String(vector3.components[1]), 'Y');
      block.setFieldValue(String(vector3.components[2]), 'Z');
    }
    
    return block;
  } catch (error) {
    console.error(`Error creating vector3 value block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create connectivity4 value block for VALUE fields
 */
export function createConnectivity4ValueBlock(connectivity4, workspace, path = '') {
  try {
    const block = createBlock(workspace, 'common_value_connectivity4', path);
    if (!block) return null;

    if (connectivity4.values && connectivity4.values.length >= 4) {
      block.setFieldValue(String(connectivity4.values[0] || 0), 'V0');
      block.setFieldValue(String(connectivity4.values[1] || 0), 'V1');
      block.setFieldValue(String(connectivity4.values[2] || 0), 'V2');
      block.setFieldValue(String(connectivity4.values[3] || 0), 'V3');
    }

    return block;
  } catch (error) {
    console.error(`Error in createConnectivity4ValueBlock at path ${path}:`, error);
    return null;
  }
}

/**
 * Create function call value block
 */
export function createFunctionCallValueBlock(functionCall, workspace, path = '') {
  try {
    const block = createBlock(workspace, 'common_function_call_value', path);
    if (!block) return null;

    if (functionCall.name && block.getField('FUNCTION_NAME')) {
      block.setFieldValue(functionCall.name, 'FUNCTION_NAME');
    }

    if (functionCall.arguments && block.getField('ARGUMENTS')) {
      const argsString = Array.isArray(functionCall.arguments) 
        ? functionCall.arguments.join(',') 
        : String(functionCall.arguments);
      block.setFieldValue(argsString, 'ARGUMENTS');
    }

    return block;
  } catch (error) {
    console.error(`Error creating function call value block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create value list block from AST
 */
export function createValueListBlock(valueList, workspace, path = '', dictName = 'common', entryKey = null) {
  try {
    console.log(`Creating value list block at path: ${path}:`, valueList);

    if (!valueList.values || valueList.values.length === 0) {
      console.warn(`ValueList has no values at path: ${path}`);
      return null;
    }

    // Always use the multi-part approach for consistency
    console.log(`Using multi-part value block for ${valueList.values.length} values at path: ${path}`);
    return createMultiPartValueListBlock(valueList, workspace, path, dictName, entryKey);
  } catch (error) {
    console.error(`Error creating value list block at path ${path}:`, error);
    return null;
  }
}

/**
 * Create multi-part value list block from AST (similar to fvSchemes enhanced valueList)
 */
export function createMultiPartValueListBlock(valueList, workspace, path = '', dictName = 'common', entryKey = null) {
  try {
    console.log(`Creating multi-part value list block at path: ${path} with ${valueList.values.length} values:`, valueList.values);

    if (!valueList.values || valueList.values.length === 0) {
      console.warn(`ValueList has no values at path: ${path}`);
      return null;
    }

    const values = valueList.values;
    
    // Handle single values specially - create simple value block directly (like fvSchemes)
    if (values.length === 1) {
      console.log(`Single value creating simple block at path: ${path}:`, values[0]);
      const singleValue = values[0];
      
      if (typeof singleValue === 'number') {
        return createSpecializedValueBlock('number', singleValue, dictName, workspace, `${path}_single_num`, entryKey);
      } else if (typeof singleValue === 'boolean') {
        return createSpecializedValueBlock('boolean', singleValue, dictName, workspace, `${path}_single_bool`, entryKey);
      } else {
        return createSpecializedValueBlock('string', singleValue, dictName, workspace, `${path}_single_str`, entryKey);
      }
    }
    
    // Create the multi-part value block for multiple values
    const multiPartBlock = createBlock(workspace, 'common_multi_part_value', path);
    if (!multiPartBlock) {
      console.error(`Failed to create multi-part value block at path: ${path}`);
      return null;
    }

    console.log(`Created common_multi_part_value block, populating with ${values.length} values`);

    // Populate the block with actual values from AST (fill from beginning, leave rest empty)
    const inputNames = ['PART1', 'PART2', 'PART3', 'PART4'];
    
    values.forEach((part, index) => {
      if (index < inputNames.length && multiPartBlock.getInput(inputNames[index])) {
        console.log(`Processing value part ${index}: "${part}"`);
        
        let valueBlock = null;
        
        // Create appropriate value block based on type
        if (typeof part === 'number') {
          valueBlock = createSpecializedValueBlock('number', part, dictName, workspace, `${path}_part${index + 1}_num`, entryKey);
        } else if (typeof part === 'boolean') {
          valueBlock = createSpecializedValueBlock('boolean', part, dictName, workspace, `${path}_part${index + 1}_bool`, entryKey);
        } else {
          valueBlock = createSpecializedValueBlock('string', part, dictName, workspace, `${path}_part${index + 1}_str`, entryKey);
        }
        
        if (valueBlock) {
          console.log(`Created value block for part "${part}"`);
          
          // Connect to input
          const input = multiPartBlock.getInput(inputNames[index]);
          if (input && input.connection && valueBlock.outputConnection) {
            input.connection.connect(valueBlock.outputConnection);
            console.log(`Connected value block to multi-part block input: ${inputNames[index]}`);
          }
        } else {
          console.error(`Failed to create value block for part: "${part}"`);
        }
      }
    });

    return multiPartBlock;
  } catch (error) {
    console.error(`Error creating multi-part value list block at path ${path}:`, error);
    return null;
  }
}

/**
 * Get available variable options from the workspace
 */
export function getAvailableVariableOptions(workspace) {
  return workspace.options?.jinjaVariableOptions || [['parameter', 'parameter']];
}

/**
 * Create a Jinja value block
 */
export function createJinjaValueBlock(value, workspace, variableOptions = [], path = '') {
  try {
    console.log(`Creating Jinja value block at path: ${path}:`, JSON.stringify(value));

    let variableName = '';
    if (value.type === 'jinja_value') {
      variableName = value.variableName || '';
    } else if (value.type === 'jinja_expression') {
      variableName = value.content || '';
    } else if (value.type === 'substitution') {
      variableName = value.value ? value.value.replace(/^\$/, '') : '';
    } else if (typeof value === 'string' && value.match(/^\{\{.*\}\}$/)) {
      variableName = value.replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
    }

    if (!variableName && value.expression) {
      variableName = value.expression.replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
    }

    if (!variableName) {
      variableName = 'parameter';
      console.log(`Using default variable name: ${variableName} at path: ${path}`);
    }

    const { createJinjaVariableBlock, getJinjaVariableOptions } = require('../jinja/jinja.blocks');
    const currentOptions = variableOptions.length > 0 
      ? variableOptions 
      : getJinjaVariableOptions();

    const isValid = currentOptions.some(option => option[0] === variableName);
    console.log(`Variable ${variableName} is ${isValid ? 'valid' : 'invalid'} in options at path: ${path}:`, currentOptions);

    const block = createJinjaVariableBlock(workspace, variableName);
    if (!block) {
      throw new Error(`Failed to create Jinja variable block for ${variableName} at path: ${path}`);
    }

    return block;
  } catch (error) {
    console.error(`Error creating Jinja value block at path ${path}:`, error);

    const invalidBlockType = 'common_value_invalid';
    if (Blockly.Blocks[invalidBlockType]) {
      const block = createBlock(workspace, invalidBlockType, path);
      if (block) {
        const displayValue = typeof value === 'object' 
          ? (value.variableName || value.content || value.expression || 'unknown')
          : (typeof value === 'string' ? value : 'unknown');
        block.setFieldValue(displayValue, 'VALUE');
      }
      return block;
    } else {
      const block = createBlock(workspace, 'common_value_string', path);
      if (block) {
        const displayValue = typeof value === 'object' 
          ? (value.variableName || value.content || value.expression || 'INVALID')
          : (typeof value === 'string' ? value : 'INVALID');
        block.setFieldValue(displayValue, 'VALUE');
        block.setWarningText('Invalid value. Please replace with a valid value.');
        block.setColour('#FF6B6B');
      }
      return block;
    }
  }
}

/**
 * Create blocks from an array of statements with BFS approach
 */
export function createBlocksFromStatements(statements, dictName, workspace, path = '', isNested = true) {
  const blocks = [];
  let prevBlock = null;

  // Import the BFS function from the main module
  const { createBlockFromNodeBFS } = require('./openfoam.astToBlock');

  for (const [index, statement] of statements.entries()) {
    const statementPath = `${path}_stmt_${index}`;
    const block = createBlockFromNodeBFS(statement, dictName, workspace, isNested, statementPath);
    
    if (block) {
      // Chain blocks together
      if (prevBlock && prevBlock.nextConnection && block.previousConnection) {
        prevBlock.nextConnection.connect(block.previousConnection);
      }
      
      blocks.push(block);
      prevBlock = block;
    }
  }

  return blocks;
}

/**
 * Create Jinja if statement blocks from AST (internal implementation)
 */
export function createJinjaIfStatementFromAstInternal(astNode, workspace, isNested = false, path = '') {
  try {
    console.log(`Creating Jinja if statement from AST at path: ${path}:`, astNode);
    
    // Determine block type based on nesting
    const blockType = isNested ? 'jinja_if_statement_nested' : 'jinja_if_statement';
    const block = createBlock(workspace, blockType, path);
    if (!block) return null;
    
    // Set up the initial condition
    if (astNode.branches && astNode.branches.length > 0) {
      const firstBranch = astNode.branches[0];
      if (firstBranch.type === 'if' && firstBranch.condition) {
        const conditionField = block.getField('CONDITION');
        if (conditionField) {
          conditionField.setValue(firstBranch.condition);
        }
      }
    }
    
    // Count elif and else branches
    let elifCount = 0;
    let elseCount = 0;
    
    if (astNode.branches) {
      elifCount = astNode.branches.filter(branch => branch.type === 'elif').length;
      elseCount = astNode.branches.filter(branch => branch.type === 'else').length > 0 ? 1 : 0;
    }
    
    // Set properties and update shape
    block.elifCount_ = elifCount;
    block.elseCount_ = elseCount;
    if (typeof block.updateShape_ === 'function') {
      block.updateShape_();
    }
    
    // Handle body content and conditions for each branch
    if (astNode.branches) {
      let elifIndex = 0;
      
      for (let i = 0; i < astNode.branches.length; i++) {
        const branch = astNode.branches[i];
        let conditionFieldName;
        
        if (branch.type === 'elif') {
          conditionFieldName = `ELIF_CONDITION${elifIndex}`;
          
          // Set the elif condition
          if (branch.condition && conditionFieldName) {
            const conditionField = block.getField(conditionFieldName);
            if (conditionField) {
              conditionField.setValue(branch.condition);
            }
          }
          
          elifIndex++;
        }
        
        // NOTE: Body content will be handled by BFS traversal in getChildNodes()
        // Don't create body blocks immediately to avoid double processing
      }
    }
    
    return block;
  } catch (error) {
    console.error(`Error creating Jinja if statement from AST at path ${path}:`, error);
    return null;
  }
}

/**
 * Create Jinja for statement blocks from AST
 */
export function createJinjaForStatementFromAst(astNode, workspace, path = '') {
  console.log(`Creating Jinja for statement from AST at path: ${path}:`, astNode);
  
  const block = createBlock(workspace, 'jinja_for_each', path);
  if (!block) return null;
  
  // Set loop variable and iterable if available
  if (astNode.loopVar && astNode.iterable) {
    // For now, just use the iterable as the variable
    const variableOptions = getAvailableVariableOptions(workspace);
    const isValid = variableOptions.some(option => option[0] === astNode.iterable);
    if (isValid && block.getField('VAR')) {
      block.setFieldValue(astNode.iterable, 'VAR');
    }
  }
  
  // Handle body if present
  if (astNode.body && Array.isArray(astNode.body)) {
    const bodyPath = `${path}_for_body`;
    const bodyBlocks = createBlocksFromStatements(astNode.body, 'jinja', workspace, bodyPath, true);
    if (bodyBlocks.length > 0) {
      const bodyInput = block.getInput('BODY');
      if (bodyInput && bodyBlocks[0].previousConnection) {
        bodyInput.connection.connect(bodyBlocks[0].previousConnection);
      }
    }
  }
  
  return block;
}

/**
 * Create Jinja set statement blocks from AST
 */
export function createJinjaSetStatementFromAst(astNode, workspace, path = '') {
  console.log(`Creating Jinja set statement from AST at path: ${path}:`, astNode);
  
  // Import the BFS function from the main module
  const { createBlockFromNodeBFS } = require('./openfoam.astToBlock');
  
  // Create a simple entry block for set statements
  const block = createBlock(workspace, 'common_entry', path);
  if (!block) return null;
  
  if (astNode.variable && block.getField('KEY')) {
    block.setFieldValue(`set ${astNode.variable}`, 'KEY');
  }
  
  // Handle value if present
  if (astNode.value) {
    const valuePath = `${path}_set_value`;
    let valueBlock;
    if (typeof astNode.value === 'object' && astNode.value.type) {
      valueBlock = createBlockFromNodeBFS(astNode.value, 'jinja', workspace, true, valuePath);
    } else {
      valueBlock = createBlock(workspace, 'common_value_string', valuePath);
      if (valueBlock) {
        valueBlock.setFieldValue(String(astNode.value), 'VALUE');
      }
    }
    
    if (valueBlock) {
      connectValueBlock(block, valueBlock, `set ${astNode.variable}`, path);
    }
  }
  
  return block;
}

/**
 * Create other Jinja statement blocks from AST
 */
export function createJinjaStatementFromAst(astNode, workspace, path = '') {
  console.log(`Creating Jinja statement from AST at path: ${path}:`, astNode);
  
  const block = createBlock(workspace, 'common_entry', path);
  if (!block) return null;
  
  // Set key based on statement type
  let key = 'jinja_statement';
  let value = '';
  
  switch (astNode.type) {
    case 'jinja_block_statement':
      key = `block ${astNode.blockName || 'unnamed'}`;
      value = astNode.body ? `{${astNode.body.length} statements}` : '';
      break;
    case 'jinja_extends_statement':
      key = 'extends';
      value = astNode.template || '';
      break;
    case 'jinja_include_statement':
      key = 'include';
      value = astNode.template || '';
      break;
    case 'jinja_macro_statement':
      key = `macro ${astNode.macroName || 'unnamed'}`;
      value = astNode.body ? `{${astNode.body.length} statements}` : '';
      break;
    case 'jinja_comment':
      key = 'comment';
      value = `{# ${astNode.content || ''} #}`;
      break;
    default:
      key = astNode.type || 'jinja_statement';
      value = JSON.stringify(astNode);
  }
  
  if (block.getField('KEY')) {
    block.setFieldValue(key, 'KEY');
  }
  
  if (value) {
    const valuePath = `${path}_jinja_value`;
    const valueBlock = createBlock(workspace, 'common_value_string', valuePath);
    if (valueBlock) {
      valueBlock.setFieldValue(value, 'VALUE');
      connectValueBlock(block, valueBlock, key, path);
    }
  }
  
  return block;
}

/**
 * Create function object block
 */
export function createFunctionObjectBlock(func, dictName, workspace, path = '') {
  try {
    const block = createBlock(workspace, 'common_function_object', path);
    if (!block) return null;
    
    block.setFieldValue(func.name, 'FUNC_NAME');

    const typeBlock = createBlock(workspace, 'common_value_string', `${path}_type`);
    if (typeBlock) {
      typeBlock.setFieldValue(func.functionType, 'VALUE');
      const typeInput = block.getInput('TYPE');
      if (typeInput) {
        typeInput.connection.connect(typeBlock.outputConnection);
      }
    }

    // Note: Entries will be handled by BFS traversal
    console.log(`Function object contains ${func.entries?.length || 0} entries - will be processed by BFS`);

    return block;
  } catch (error) {
    console.error(`Error in createFunctionObjectBlock at path ${path}:`, error);
    return null;
  }
}

/**
 * Get specialized value block type based on dictionary name and entry key
 */
export function getSpecializedValueBlockType(valueType, dictName, entryKey = null, dictModule = null) {
  try {
    // Get dict module if not provided
    if (!dictModule) {
      dictModule = dictRegistry.get(dictName);
    }

    // If dictionary module provides a method to get value block types, use it
    if (dictModule && typeof dictModule.getValueBlockType === 'function') {
      const specializedType = dictModule.getValueBlockType(valueType, entryKey);
      if (specializedType && Blockly.Blocks[specializedType]) {
        console.log(`Dictionary module provided specialized value block: ${specializedType}`);
        return specializedType;
      }
    }

    // If dictionary module has value block mappings, check them
    if (dictModule && dictModule.VALUE_BLOCK_MAPPINGS) {
      const mapping = dictModule.VALUE_BLOCK_MAPPINGS[entryKey];
      if (mapping && mapping[valueType] && Blockly.Blocks[mapping[valueType]]) {
        console.log(`Found specialized value block from mappings: ${mapping[valueType]}`);
        return mapping[valueType];
      }
    }

    // Check for common dictionary-specific patterns
    const dictPrefix = dictModule?.prefix || dictName;
    const candidateTypes = [];

    if (entryKey) {
      // Try patterns like: dictName_value_entryKey, dictName_entryKey_value
      candidateTypes.push(`${dictPrefix}_value_${entryKey}`);
      candidateTypes.push(`${dictPrefix}_${entryKey}_value`);
      candidateTypes.push(`${dictPrefix}_${entryKey}_${valueType}`);
    }

    // Try general patterns: dictName_value_type
    candidateTypes.push(`${dictPrefix}_value_${valueType}`);
    candidateTypes.push(`${dictName}_value_${valueType}`);

    // Check if any candidate types exist
    for (const candidateType of candidateTypes) {
      if (Blockly.Blocks[candidateType]) {
        console.log(`Found specialized value block: ${candidateType}`);
        return candidateType;
      }
    }

    console.log(`No specialized value block found for ${valueType} in ${dictName}${entryKey ? ` (key: ${entryKey})` : ''}, using common block`);
    return null; // Will fall back to common block
  } catch (error) {
    console.error(`Error getting specialized value block type:`, error);
    return null;
  }
}

/**
 * Create specialized value block based on dictionary context
 */
export function createSpecializedValueBlock(valueType, value, dictName, workspace, path, entryKey = null) {
  const blockType = getSpecializedValueBlockType(valueType, dictName, entryKey) || `common_value_${valueType}`;
  console.log(`Creating specialized ${valueType} value block: ${blockType} for value "${value}" at path: ${path}`);
  
  const block = createBlock(workspace, blockType, path);
  if (block) {
    block.setFieldValue(String(value), 'VALUE');
  }
  return block;
}

